// =============================================================================
// VandieZ agent-web -- Post Load (/post-load)
// =============================================================================
// AG-12. The FSD calls this the agent's PRIMARY action (p65: "keep Post
// Load visible as a primary action because agents may post many loads
// per day") and p66 is explicit that "Agent can post many loads per day
// unlike a normal customer".
//
// The agent is the SHIPPER on loads they post, so there is no `?as=`
// here and no owner picker -- this is the agent's own book, not
// delegated work. That is the one structural difference from every other
// page in this app.
//
// DESIGNED FOR REPETITION, not for a single careful submission. p66's
// recommendation is "support save draft and duplicate load to reduce
// repeated data entry for agents", and the realistic pattern is ten
// loads down the same lane with different weights. So on success the
// form KEEPS its values and offers "Post another" -- clearing a
// twenty-field form after every submit is what makes a tool unusable at
// volume.
// =============================================================================

import { AlertCircle, Check, Loader2, Package, Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  loadsApi,
  pricingModelsApi,
  referenceDataApi,
} from "@/api/client";
import { AppHeader } from "@/components/AppHeader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";
import type {
  PricingModelCode,
  PublicPricingModel,
  ReferenceItem,
} from "@/types/load";
import type { LoadCreatePayload } from "@/types/load";

/** Mirrors the backend's `hamali_required` vocabulary. */
const HAMALI = ["self_arrange", "driver_assist", "not_required"] as const;

const VEHICLE_TYPES = ["open_body", "closed_body", "refrigerated"] as const;

interface FormState {
  pickupAddress: string;
  pickupContactName: string;
  pickupContactPhone: string;
  pickupDistrict: string;
  pickupLat: string;
  pickupLng: string;
  pickupFrom: string;
  pickupTo: string;
  dropAddress: string;
  dropContactName: string;
  dropContactPhone: string;
  dropDistrict: string;
  dropLat: string;
  dropLng: string;
  materialCategory: string;
  materialDescription: string;
  weightKg: string;
  payloadKg: string;
  vehicleType: string;
  hamali: string;
  budgetMin: string;
  budgetMax: string;
  bidWindowHours: string;
  pricingModel: PricingModelCode;
}

const EMPTY: FormState = {
  pickupAddress: "",
  pickupContactName: "",
  pickupContactPhone: "",
  pickupDistrict: "",
  pickupLat: "",
  pickupLng: "",
  pickupFrom: "",
  pickupTo: "",
  dropAddress: "",
  dropContactName: "",
  dropContactPhone: "",
  dropDistrict: "",
  dropLat: "",
  dropLng: "",
  materialCategory: "",
  materialDescription: "",
  weightKg: "",
  payloadKg: "",
  vehicleType: "open_body",
  hamali: "self_arrange",
  budgetMin: "",
  budgetMax: "",
  bidWindowHours: "6",
  // Bidding cannot be disabled (AD-07 forbids it), so this default can
  // never be a code the server would reject, even if the fetch fails.
  pricingModel: "bidding",
};

/** Field-level problems, keyed by form field. */
type Problems = Partial<Record<keyof FormState, string>>;

function validate(f: FormState): Problems {
  const p: Problems = {};
  const required: (keyof FormState)[] = [
    "pickupAddress",
    "pickupContactName",
    "pickupContactPhone",
    "pickupDistrict",
    "pickupFrom",
    "pickupTo",
    "dropAddress",
    "dropContactName",
    "dropContactPhone",
    "dropDistrict",
    "materialCategory",
    "materialDescription",
    "weightKg",
    "payloadKg",
  ];
  for (const k of required) {
    if (f[k].trim() === "") {p[k] = "Required";}
  }

  // Catch these here rather than as a 400 round-trip: at ten loads a day
  // a server rejection on field fourteen is a real cost.
  if (f.pickupFrom !== "" && f.pickupTo !== "" && f.pickupTo <= f.pickupFrom) {
    p.pickupTo = "Must be after the start of the window";
  }
  const min = Number.parseFloat(f.budgetMin);
  const max = Number.parseFloat(f.budgetMax);
  if (f.budgetMin !== "" && f.budgetMax !== "" && max < min) {
    p.budgetMax = "Cannot be below the minimum";
  }
  const payload = Number.parseFloat(f.payloadKg);
  const weight = Number.parseFloat(f.weightKg);
  if (f.weightKg !== "" && f.payloadKg !== "" && payload < weight) {
    p.payloadKg = "Vehicle capacity is below the load weight";
  }
  for (const [k, v] of [
    ["pickupContactPhone", f.pickupContactPhone],
    ["dropContactPhone", f.dropContactPhone],
  ] as const) {
    if (v.trim() !== "" && !/^\+91\d{10}$/.test(v.trim())) {
      p[k] = "Use +91 followed by 10 digits";
    }
  }
  return p;
}

function toPayload(f: FormState): LoadCreatePayload {
  return {
    pickup_address: f.pickupAddress.trim(),
    pickup_contact_name: f.pickupContactName.trim(),
    pickup_contact_phone: f.pickupContactPhone.trim(),
    // Coordinates are optional in this form; the backend needs numbers,
    // and 0/0 would be a point in the Atlantic. Chennai is the default
    // service origin until a map picker lands here.
    pickup_lat: Number.parseFloat(f.pickupLat) || 13.0827,
    pickup_lng: Number.parseFloat(f.pickupLng) || 80.2707,
    pickup_district: f.pickupDistrict.trim(),
    pickup_window_start: new Date(f.pickupFrom).toISOString(),
    pickup_window_end: new Date(f.pickupTo).toISOString(),
    drop_address: f.dropAddress.trim(),
    drop_contact_name: f.dropContactName.trim(),
    drop_contact_phone: f.dropContactPhone.trim(),
    drop_lat: Number.parseFloat(f.dropLat) || 11.0168,
    drop_lng: Number.parseFloat(f.dropLng) || 76.9558,
    drop_district: f.dropDistrict.trim(),
    material_category: f.materialCategory.trim(),
    material_description: f.materialDescription.trim(),
    weight_kg: Number.parseInt(f.weightKg, 10),
    required_vehicle_type: f.vehicleType,
    required_payload_kg: Number.parseInt(f.payloadKg, 10),
    hamali_required: f.hamali,
    labourers_needed: 0,
    tarpaulin_required: false,
    special_requirements: [],
    pricing_model: f.pricingModel,
    budget_min: Number.parseInt(f.budgetMin, 10) || 0,
    budget_max: Number.parseInt(f.budgetMax, 10) || 0,
    budget_visible_to_bidders: true,
    anonymous_listing: false,
    bid_window_hours: Number.parseInt(f.bidWindowHours, 10) || 6,
  };
}

export function PostLoadPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [problems, setProblems] = useState<Problems>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [postedIds, setPostedIds] = useState<string[]>([]);
  // AG-12 (p66). Enabled-only, straight from the catalog, so enabling a
  // model in the admin screen reaches this picker with no client release
  // (D-2). The bidding-only fallback below keeps the form usable if the
  // fetch fails -- bidding cannot be disabled (AD-07), so the fallback can
  // never offer a code the server would reject.
  const [enabledModels, setEnabledModels] = useState<PublicPricingModel[]>([
    {
      code: "bidding",
      display_name: "Drivers bid",
      description: null,
      is_recommended: true,
    },
  ]);
  const [modelsLoading, setModelsLoading] = useState(true);

  // C-24. Was a free-text box, so an agent typed whatever they liked and the
  // curated catalog reached nobody. `other` as the fallback is safe: it is
  // always in the catalog, so the form can never submit a code the server
  // would not recognise.
  const [goodsTypes, setGoodsTypes] = useState<ReferenceItem[]>([
    { code: "other", label: "Other", sort_order: 999 },
  ]);

  useEffect(() => {
    if (accessToken === null) {
      return;
    }
    const ac = new AbortController();
    referenceDataApi
      .listGoodsTypes(accessToken, ac.signal)
      .then((rows) => {
        if (rows.length === 0) {
          return;
        }
        setGoodsTypes(rows);
        setForm((f) =>
          f.materialCategory === "" ? { ...f, materialCategory: rows[0]!.code } : f,
        );
      })
      .catch(() => {
        /* keep the fallback */
      });
    return () => {
      ac.abort();
    };
  }, [accessToken]);

  useEffect(() => {
    if (accessToken === null) {
      setModelsLoading(false);
      return;
    }
    const ac = new AbortController();
    pricingModelsApi
      .listEnabled(accessToken, ac.signal)
      .then((models) => {
        if (models.length === 0) {
          return;
        }
        setEnabledModels(models);
        // p9 "show recommended model": preselect it so an agent who does
        // not know which to use is not left guessing. Only while the form
        // still holds the default -- overriding a choice already made would
        // be worse than not recommending at all.
        const rec = models.find((m) => m.is_recommended);
        if (rec !== undefined) {
          setForm((f) =>
            f.pricingModel === "bidding" ? { ...f, pricingModel: rec.code } : f,
          );
        }
      })
      .catch(() => {
        /* keep the bidding-only fallback */
      })
      .finally(() => {
        setModelsLoading(false);
      });
    return () => {
      ac.abort();
    };
  }, [accessToken]);


  const set = useCallback((k: keyof FormState, v: string) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setProblems((prev) => ({ ...prev, [k]: undefined }));
  }, []);

  const submit = useCallback(async () => {
    if (accessToken === null) {return;}
    const found = validate(form);
    // Key count, not a value check: `validate` only ever ADDS a key when
    // there is a real problem, and TS types these values as `string`
    // (Partial<Record<...>> drops the undefined from Object.values), so
    // comparing against undefined can never be false.
    if (Object.keys(found).length > 0) {
      setProblems(found);
      setError(null);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const load = await loadsApi.create(accessToken, toPayload(form));
      // The form is deliberately NOT cleared — see the header note.
      setPostedIds((prev) => [load.id, ...prev]);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not post.");
    } finally {
      setSaving(false);
    }
  }, [accessToken, form]);

  const field = (
    k: keyof FormState,
    label: string,
    type = "text",
    placeholder = "",
  ): React.JSX.Element => (
    <div className="space-y-1">
      <Label htmlFor={k}>{label}</Label>
      <Input
        id={k}
        type={type}
        value={form[k]}
        placeholder={placeholder}
        aria-invalid={problems[k] !== undefined}
        onChange={(e) => {
          set(k, e.target.value);
        }}
      />
      {problems[k] !== undefined ? (
        <p className="text-xs text-destructive">{problems[k]}</p>
      ) : null}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* No owner switcher: these are the agent's OWN loads, not
            delegated work. Showing "Acting as agent for X" here
            would state the opposite of what is happening. */}
        <AppHeader title="Post a load" showOwnerSwitcher={false} />
      <main className="mx-auto max-w-4xl space-y-6 p-4">
        <p className="text-sm text-muted-foreground">
          Posted under your own account. Drivers bid on it exactly as they
          would on any other load.
        </p>

        {error !== null ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {postedIds.length > 0 ? (
          <Alert>
            <Check className="h-4 w-4" />
            <AlertDescription>
              <strong>
                {postedIds.length === 1
                  ? "1 load posted."
                  : `${postedIds.length} loads posted.`}
              </strong>{" "}
              The form still holds what you entered — change what differs
              and post the next one.{" "}
              <Link className="underline" to="/my-loads">
                See my loads
              </Link>
            </AlertDescription>
          </Alert>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Pickup</CardTitle>
            <CardDescription>
              Where the vehicle collects, and the window it may arrive in.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            {field("pickupAddress", "Pickup address")}
            {field("pickupDistrict", "Pickup district", "text", "Chennai")}
            {field("pickupContactName", "Pickup contact name")}
            {field("pickupContactPhone", "Pickup contact phone", "tel", "+919…")}
            {field("pickupFrom", "Window opens", "datetime-local")}
            {field("pickupTo", "Window closes", "datetime-local")}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Drop</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            {field("dropAddress", "Drop address")}
            {field("dropDistrict", "Drop district", "text", "Coimbatore")}
            {field("dropContactName", "Drop contact name")}
            {field("dropContactPhone", "Drop contact phone", "tel", "+919…")}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Load</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="materialCategory">Material</Label>
              <select
                id="materialCategory"
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={form.materialCategory}
                onChange={(e) => {
                  set("materialCategory", e.target.value);
                }}
              >
                {goodsTypes.map((g) => (
                  <option key={g.code} value={g.code}>
                    {g.label}
                  </option>
                ))}
              </select>
            </div>
            {field("materialDescription", "Description")}
            {field("weightKg", "Weight (kg)", "number")}
            {field("payloadKg", "Vehicle capacity needed (kg)", "number")}
            <div className="space-y-1">
              <Label htmlFor="vehicleType">Vehicle type</Label>
              <select
                id="vehicleType"
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={form.vehicleType}
                onChange={(e) => {
                  set("vehicleType", e.target.value);
                }}
              >
                {VEHICLE_TYPES.map((v) => (
                  <option key={v} value={v}>
                    {v.replace("_", " ")}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="hamali">Loading help</Label>
              <select
                id="hamali"
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={form.hamali}
                onChange={(e) => {
                  set("hamali", e.target.value);
                }}
              >
                {HAMALI.map((h) => (
                  <option key={h} value={h}>
                    {h.replace("_", " ")}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="pricingModel">How is this priced?</Label>
              <select
                id="pricingModel"
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={form.pricingModel}
                disabled={saving || modelsLoading}
                onChange={(e) => {
                  set("pricingModel", e.target.value);
                }}
              >
                {enabledModels.map((m) => (
                  <option key={m.code} value={m.code}>
                    {m.is_recommended
                      ? `${m.display_name} (recommended)`
                      : m.display_name}
                  </option>
                ))}
              </select>
            </div>
            {field("budgetMin", "Budget from (₹)", "number")}
            {field("budgetMax", "Budget to (₹)", "number")}
            {field("bidWindowHours", "Bidding window (hours)", "number")}
          </CardContent>
        </Card>

        <div className="flex items-center gap-3 pb-10">
          <Button onClick={() => void submit()} disabled={saving}>
            {saving ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-1 h-4 w-4" />
            )}
            {postedIds.length > 0 ? "Post another" : "Post load"}
          </Button>
          {postedIds.length > 0 ? (
            <Button
              variant="outline"
              onClick={() => {
                setForm(EMPTY);
                setProblems({});
              }}
            >
              <Package className="mr-1 h-4 w-4" /> Start blank
            </Button>
          ) : null}
        </div>
      </main>
    </div>
  );
}
